import { prop, values, uniq, filter } from 'ramda'

// Background image preloader.
//
// The hover side-panel (see ScreeningHoverDetailRight / getScreeningImage) and
// the screening detail gallery render REMOTE movie posters/stills:
//   - movies[].image / movies[].imageAlt
//   - movieCsfdData[].images[] (csfd photos + poster)
// The cinema maps (src/images/maps/*.png) are bundled webpack assets and are
// already fast, so we deliberately skip them.
//
// Those remote URLs are only fetched when a screening is hovered/opened, which
// causes a several-hundred-ms flash. After the data has loaded we warm the
// browser HTTP cache for every remote URL in the background so later renders
// are instant.

const isRemote = url => typeof url === 'string' && /^https?:\/\//.test(url)

// Build a de-duplicated, priority-ordered list of remote image URLs.
// Ordering: walk the upcoming screenings in date order (they are already sorted
// in fetchShowtimes) and, for each screening's movie, emit its hover image
// first, then its remaining csfd photos. Whatever is left over is appended at
// the end. Soonest screenings sit at the top of the list, so their images are
// warmed first - that is what the user scrolls past first.
const collectImageUrls = ({ movies, movieCsfdData, screenings } = {}) => {
  const moviesById = movies || {}
  const csfdByTitle = movieCsfdData || {}

  const csfdImagesFor = movie => {
    const csfd = movie && csfdByTitle[movie.canonicalTitle]
    return (csfd && csfd.images) || []
  }

  const ordered = []

  const pushMovie = movie => {
    if (!movie) return
    const csfdImages = csfdImagesFor(movie)
    // Mirror getScreeningImage's fallback order for the "first, most-likely-seen"
    // image, then the rest of the csfd gallery.
    ordered.push(movie.image, csfdImages[0], movie.imageAlt, ...csfdImages)
  }

  ;(screenings || []).forEach(screening => {
    pushMovie(moviesById[prop('movie', screening)])
  })

  // Catch any movie / csfd entry not referenced by an upcoming screening.
  values(moviesById).forEach(pushMovie)
  values(csfdByTitle).forEach(csfd => {
    ;(csfd && csfd.images ? csfd.images : []).forEach(url => ordered.push(url))
  })

  return uniq(filter(isRemote, ordered))
}

// Warm a single URL into the browser cache. `new Image()` is enough - the
// browser stores the response and a later `background: url(...)` / <img> render
// reuses it from cache. Failures are ignored on purpose.
const warm = url =>
  new Promise(resolve => {
    const img = new Image()
    img.onload = resolve
    img.onerror = resolve
    img.src = url
  })

// Drain `urls` with at most `concurrency` requests in flight so we never
// saturate the connection or compete with the initial render.
const runPool = (urls, concurrency) => {
  let i = 0
  const worker = () => {
    if (i >= urls.length) return Promise.resolve()
    const url = urls[i++]
    return warm(url).then(worker)
  }
  const size = Math.min(concurrency, urls.length)
  return Promise.all(Array.from({ length: size }, worker))
}

const onIdle = fn => {
  if (typeof window === 'undefined') return
  if (typeof window.requestIdleCallback === 'function') {
    window.requestIdleCallback(fn, { timeout: 2000 })
  } else {
    // Safari and friends: run shortly after first paint.
    setTimeout(fn, 200)
  }
}

let started = false

// Kick off a one-shot, non-blocking background preload of every remote movie
// image. Safe to call more than once - it only runs the first time.
const preloadImages = (data, { concurrency = 5 } = {}) => {
  if (started || typeof window === 'undefined') return
  started = true

  onIdle(() => {
    const urls = collectImageUrls(data)
    if (urls.length) runPool(urls, concurrency)
  })
}

export { preloadImages, collectImageUrls }
export default preloadImages
